"use client";

import Link from "next/link";
import Modal from "@/components/ui/Modal";
import Button from "@/components/ui/Button";
import { isPwaPostInstallPushPending } from "@/components/PwaPostInstallPushPrompt";
import {
  PROFILE_LOCATION_SETUP_HREF,
  PROFILE_NOTIFICATION_SETUP_HREF,
  type NotificationSetupBannerMode,
} from "@/lib/notifications/notification-setup-eligibility";
import type { NotificationSetupGaps } from "@/lib/notifications/notification-setup-eligibility";
import {
  PUSH_NOTIFICATIONS_HELP_HREF,
  pushPermissionHelpHint,
} from "@/lib/notifications/push-settings-help";

type NotificationSetupSheetProps = {
  isOpen: boolean;
  onClose: () => void;
  mode: NotificationSetupBannerMode;
  gaps: NotificationSetupGaps;
  locationOk: boolean;
  nearMeBusy: boolean;
  pushBusy: boolean;
  pushSupported: boolean;
  pushVapidConfigured: boolean;
  pushPermission: NotificationPermission | "unsupported";
  pushLastError: string | null;
  onEnableNearMe: () => void;
  onEnablePush: () => void;
  onFinishSetup: () => void;
  finishBusy: boolean;
};

function sheetTitle(mode: NotificationSetupBannerMode): string {
  if (mode === "push") {
    return "Enable notifications on this device";
  }
  if (mode === "near_me") {
    return "Turn on near-me alerts";
  }
  return "Finish your alert setup";
}

export default function NotificationSetupSheet({
  isOpen,
  onClose,
  mode,
  gaps,
  locationOk,
  nearMeBusy,
  pushBusy,
  pushSupported,
  pushVapidConfigured,
  pushPermission,
  pushLastError,
  onEnableNearMe,
  onEnablePush,
  onFinishSetup,
  finishBusy,
}: NotificationSetupSheetProps) {
  const pwaPushPending = isPwaPostInstallPushPending();
  const showNearMeSection = gaps.nearMeGap;
  const showPushSection = gaps.pushGap;
  const bothGaps = showNearMeSection && showPushSection;
  const pushBlockedByPwa = pwaPushPending && showPushSection;
  const pushActionable =
    showPushSection &&
    pushSupported &&
    pushVapidConfigured &&
    pushPermission !== "denied" &&
    !pushBlockedByPwa;
  const actionBusy = finishBusy || nearMeBusy || pushBusy;

  const showLocationCta = showNearMeSection && !locationOk;
  const showFooterPrimary =
    bothGaps ||
    (showPushSection && pushActionable && !showNearMeSection) ||
    (showNearMeSection && locationOk && !showPushSection);

  const primaryLabel = bothGaps
    ? "Turn on all alerts"
    : showPushSection && !showNearMeSection
      ? "Enable notifications"
      : showNearMeSection && locationOk
        ? "Turn on near-me alerts"
        : null;

  const handlePrimaryAction = () => {
    if (bothGaps) {
      void onFinishSetup();
      return;
    }
    if (showPushSection && pushActionable) {
      void onEnablePush();
      return;
    }
    if (showNearMeSection && locationOk) {
      void onEnableNearMe();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={sheetTitle(mode)}
      maxWidth="sm"
    >
      <div className="space-y-5 text-sm text-text-light">
        <div className="rounded-md border border-border bg-surface px-3 py-2 text-xs text-text-light">
          <p className="font-medium text-foreground mb-1.5">
            How alerts reach you
          </p>
          <ul className="space-y-1">
            <li>
              <span className="font-medium text-foreground">In-app:</span>{" "}
              always on when a category is enabled
            </li>
            <li>
              <span className="font-medium text-foreground">Email:</span>{" "}
              optional per category in Profile
            </li>
            <li>
              <span className="font-medium text-foreground">Push:</span>{" "}
              optional on this device for instant delivery
            </li>
          </ul>
        </div>

        {showNearMeSection ? (
          <section className="space-y-2 rounded-lg border border-border bg-secondary/80 p-3">
            <h3 className="text-sm font-medium text-foreground">
              Alerts near you
            </h3>
            <p className="text-xs text-text-light">
              Get local listing alerts when matches are approved near you —
              inbox and email first; push adds instant delivery on this device.
            </p>
            {showLocationCta ? (
              <Link href={PROFILE_LOCATION_SETUP_HREF} onClick={onClose}>
                <Button
                  type="button"
                  variant="primary"
                  className="mt-1 w-full text-white"
                >
                  Add profile location
                </Button>
              </Link>
            ) : null}
          </section>
        ) : null}

        {showPushSection ? (
          <section className="space-y-2 rounded-lg border border-border bg-secondary/80 p-3">
            <h3 className="text-sm font-medium text-foreground">
              Instant alerts on this device
            </h3>
            <p className="text-xs text-text-light">
              Works with orders, listing updates, and near-me matches.
            </p>
            {pushBlockedByPwa ? (
              <p className="text-xs text-amber-800">
                Finish the app install notification prompt first, then enable
                push here.
              </p>
            ) : null}
            {!pushSupported || !pushVapidConfigured ? (
              <p className="text-xs text-text-light">
                Push is not available in this browser or the app is not
                configured for it.
              </p>
            ) : pushPermission === "denied" ? (
              <p className="text-xs text-amber-800">
                Notifications are blocked in your browser settings.{" "}
                <Link
                  href={PUSH_NOTIFICATIONS_HELP_HREF}
                  className="font-medium text-primary hover:underline"
                  onClick={onClose}
                >
                  {pushPermissionHelpHint()}
                </Link>
              </p>
            ) : null}
            {pushLastError ? (
              <p className="text-xs text-red-600">{pushLastError}</p>
            ) : null}
          </section>
        ) : null}

        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button type="button" variant="secondary" onClick={onClose}>
            Not now
          </Button>
          {showFooterPrimary && primaryLabel ? (
            <Button
              type="button"
              onClick={handlePrimaryAction}
              disabled={actionBusy}
              isLoading={actionBusy}
            >
              {primaryLabel}
            </Button>
          ) : null}
        </div>

        <p className="text-center text-xs text-text-light">
          Need more control?{" "}
          <Link
            href={PROFILE_NOTIFICATION_SETUP_HREF}
            className="font-medium text-primary hover:underline"
            onClick={onClose}
          >
            Open Profile settings
          </Link>
        </p>
      </div>
    </Modal>
  );
}
